/**
 * `RestCollectionFetcher` — a one-shot, complete-set replacement for an
 * org-wide live `CollectionListener`, with the same consumer surface
 * (`value` / `initialising` / `error` / `listen` / `get` / `asTuple`) so call
 * sites don't change.
 *
 * Semantics:
 * - Lazy: nothing is fetched until the first `get()` / hook mount.
 * - Complete: the fetch function resolves only with the FULL set.
 * - One in-flight fetch shared by every caller. (The SDK-backed
 *   `CollectionListener.get()` could hang: waiters unsubscribed themselves
 *   inside `notify`'s forEach, skipping the next waiter.)
 * - Stale-while-revalidate: once loaded, `value` stays on screen and
 *   `initialising` stays false while a background refresh runs.
 * - Errors are not sticky: with no value yet, the next `get()` / mount retries;
 *   with a value, a failed refresh keeps the previous value.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { useEffect, useState } from 'react';

type Listener<T> = (self: RestCollectionFetcher<T>) => void;

export type RestCollectionFetcherOptions = {
  /** Resolve to a loaded, empty set without fetching (e.g. absent orgId). */
  disabled?: boolean;
  /** A mount / get() on data older than this triggers a background refresh. */
  maxAgeMs?: number;
  now?: () => number;
};

const DEFAULT_MAX_AGE_MS = 10 * 60 * 1000;

export class RestCollectionFetcher<T> {
  initialising = true;
  value: Doc<T>[] | void = undefined;
  error: any | undefined = undefined;
  fetchedAt = 0;

  private inFlight: Promise<Doc<T>[]> | null = null;
  private listeners = new Set<Listener<T>>();
  private readonly disabled: boolean;
  private readonly maxAgeMs: number;
  private readonly now: () => number;

  constructor(
    private readonly fetchAll: () => Promise<Doc<T>[]>,
    options: RestCollectionFetcherOptions = {}
  ) {
    this.disabled = !!options.disabled;
    this.maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
    this.now = options.now || Date.now;
    if (this.disabled) {
      this.value = [];
      this.initialising = false;
    }
  }

  private notify = () => {
    // Iterate a copy: a listener may unsubscribe itself while being notified.
    Array.from(this.listeners).forEach((l) => l(this));
  };

  listen = (listener: Listener<T>) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  isStale = () => !!this.value && this.now() - this.fetchedAt > this.maxAgeMs;

  /**
   * Fetch now (or join the fetch already running). Keeps the current value
   * visible while refreshing.
   */
  refresh = (): Promise<Doc<T>[]> => {
    if (this.disabled) return Promise.resolve([]);
    if (this.inFlight) return this.inFlight;
    if (!this.value && this.error) {
      // Retrying after a failed first load: show loading again.
      this.error = undefined;
      this.initialising = true;
      this.notify();
    }
    const p = this.fetchAll().then(
      (docs) => {
        this.value = docs;
        this.error = undefined;
        this.initialising = false;
        this.fetchedAt = this.now();
        this.inFlight = null;
        this.notify();
        return docs;
      },
      (err) => {
        this.inFlight = null;
        if (!this.value) {
          this.error = err;
          this.initialising = false;
          this.notify();
        } else {
          // Keep showing the last good set; a later refresh can recover.
          // eslint-disable-next-line no-console
          console.warn('RestCollectionFetcher refresh failed; keeping previous value', err);
        }
        throw err;
      }
    );
    this.inFlight = p;
    return p;
  };

  /** Start the first load, or refresh in the background when stale. */
  ensureFresh = () => {
    if (this.disabled) return;
    if (!this.value || this.isStale()) {
      this.refresh().catch(() => undefined);
    }
  };

  get = (): Promise<Doc<T>[]> => {
    if (this.value) {
      if (this.isStale()) this.refresh().catch(() => undefined);
      return Promise.resolve(this.value);
    }
    return this.refresh();
  };

  asTuple = () => {
    const { value, initialising, error } = this;
    return [value, initialising, error] as [typeof value, boolean, any];
  };
}

/** Lazily-created fetcher per key (session singleton per key). */
export const createRestCollectionStore = <T>(
  create: (key: string) => RestCollectionFetcher<T>
) => {
  const store: { [key: string]: RestCollectionFetcher<T> } = {};
  const get = (key: string): RestCollectionFetcher<T> =>
    (store[key] = store[key] || create(key));
  /** Existing fetcher for a key, without creating one. */
  get.peek = (key: string): RestCollectionFetcher<T> | undefined => store[key];
  return get;
};

/**
 * `[value, loading, error]` for a fetcher. Returns already-cached data on the
 * FIRST render (some consumers seed `useState` from it), and kicks off the load
 * (or a stale background refresh) on mount.
 */
export const useRestCollection = <T>(c: RestCollectionFetcher<T>) => {
  const [state, setState] = useState(c.asTuple());
  useEffect(() => {
    const unsub = c.listen((x) => setState(x.asTuple()));
    setState(c.asTuple());
    c.ensureFresh();
    return unsub;
  }, [c]);
  return state;
};
